import { inject, Injectable } from '@angular/core';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { append, iif, patch, updateItem } from '@ngxs/store/operators';
import { catchError, filter, tap } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import { PaginationState } from '@apps/clos-sessions/shared/pagination/types';
import { ClosArchive, ClosArchiveInspection } from './types';
import {
  CreateArchive,
  FetchArchives,
  FetchArchivesNextPaginationChunk,
  FetchInspection,
  RemoveArchives,
  SetArchivePrice,
  SyncArchive,
} from './archives.actions';
import { ArchivesApi } from './archives.api';
import { SignOut } from '@apps/clos-sessions/state/auth';

export interface ArchivesStateModel {
  archives: ClosArchive[];
  inspections: { [archiveId: string]: ClosArchiveInspection };
  paginationState: PaginationState;
}

const defaults: ArchivesStateModel = {
  archives: [],
  inspections: {},
  paginationState: PaginationState.unknown,
};

@State<ArchivesStateModel>({
  name: 'archives',
  defaults,
})
@Injectable()
export class ArchivesState {
  static paginationState = createSelector(
    [ArchivesState],
    ({ paginationState }: ArchivesStateModel) => paginationState,
  );

  static archives = createSelector([ArchivesState], ({ archives }: ArchivesStateModel) => archives);

  static inspections = createSelector(
    [ArchivesState],
    ({ inspections }: ArchivesStateModel) => inspections,
  );

  static inspectionById = (archiveId: string) => {
    return createSelector(
      [ArchivesState.inspections],
      (inspections: ArchivesStateModel['inspections']) => inspections[archiveId] || null,
    );
  };

  static archiveByGuid = (guid: string) => {
    return createSelector([ArchivesState.archives], (archives: ClosArchive[]) => {
      return archives.find((archive) => guid === archive.guid) || null;
    });
  };

  protected listGeneration = 0;

  protected archivesApi = inject(ArchivesApi);

  protected chunkSize = 10;

  @Action(RemoveArchives)
  onRemoveArchives(
    { patchState, getState }: StateContext<ArchivesStateModel>,
    { payload }: RemoveArchives,
  ) {
    return this.archivesApi.bulkRemoveById(payload.archiveIds).pipe(
      tap(() => {
        patchState({
          archives: getState().archives.filter(({ id }) => !payload.archiveIds.includes(id)),
        });
      }),
    );
  }

  @Action(CreateArchive)
  onCreateArchive(
    { patchState, getState }: StateContext<ArchivesStateModel>,
    { payload }: CreateArchive,
  ) {
    return this.archivesApi.createArchive(payload).pipe(
      tap((closArchive) => {
        patchState({
          archives: [closArchive, ...getState().archives],
        });
      }),
    );
  }

  @Action(FetchArchives, { cancelUncompleted: true })
  onFetchArchives({ patchState }: StateContext<ArchivesStateModel>) {
    this.listGeneration += 1;
    patchState({ paginationState: PaginationState.unknown });
    return this.archivesApi
      .getArchives({
        after: 0,
        take: 20,
      })
      .pipe(
        tap(({ hasNextChunk, list }) => {
          patchState({
            archives: list,
            paginationState: hasNextChunk ? PaginationState.hasMore : PaginationState.complete,
          });
        }),
      );
  }

  @Action(FetchArchivesNextPaginationChunk, { cancelUncompleted: true })
  protected onFetchArchivesNextPaginationChunk({
    patchState,
    getState,
  }: StateContext<ArchivesStateModel>) {
    const { archives, paginationState } = getState();
    if (paginationState !== PaginationState.hasMore) {
      return EMPTY;
    }
    const generation = this.listGeneration;

    return this.archivesApi
      .getArchives({
        after: getState().archives.length,
        take: this.chunkSize,
      })
      .pipe(
        // A refresh invalidates pages requested against the previous list.
        filter(() => generation === this.listGeneration),
        catchError((error) =>
          generation === this.listGeneration ? throwError(() => error) : EMPTY,
        ),
        tap(({ hasNextChunk, list }) => {
          patchState({
            archives: [...archives, ...list],
            paginationState: hasNextChunk ? PaginationState.hasMore : PaginationState.complete,
          });
        }),
      );
  }

  @Action(SyncArchive)
  protected onSyncArchive(
    { getState, setState }: StateContext<ArchivesStateModel>,
    { payload }: SyncArchive,
  ) {
    return this.archivesApi.getArchiveById(payload.archive.id).pipe(
      tap((archive) => {
        const exists = getState().archives.some(({ id }) => id === archive.id);

        setState(
          patch({
            archives: iif(
              exists,
              updateItem(({ id }) => id === archive.id, archive),
              append([archive]),
            ),
            inspections: { ...getState().inspections, [archive.id]: archive },
          }),
        );
      }),
    );
  }

  @Action(FetchInspection)
  protected onFetchInspection(
    { getState, patchState }: StateContext<ArchivesStateModel>,
    { payload }: FetchInspection,
  ) {
    return this.archivesApi.getArchiveById(payload.archive.id).pipe(
      tap((archive) => {
        patchState({
          inspections: { ...getState().inspections, [archive.id]: archive },
        });
      }),
    );
  }

  @Action(SetArchivePrice)
  protected onSetArchivePrice(_: StateContext<ArchivesStateModel>, { payload }: SetArchivePrice) {
    return this.archivesApi.setRoomPrice(payload);
  }

  /** Account-scoped data must not survive a logout (the next account can sign in without a reload). */
  @Action(SignOut)
  protected onSignOut({ setState }: StateContext<ArchivesStateModel>): void {
    setState(defaults);
  }
}
